import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Lightbulb, MessageCircleHeart, EyeOff, CornerDownRight, RefreshCw, Inbox,
  School, Trash2, Mail, Phone, MapPin, Users, ArrowUpRight,
} from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { AdminSegmentedControl } from "@/components/admin/AdminFilterBar";
import AdminButton from "@/components/admin/AdminButton";
import AdminBadge from "@/components/admin/AdminBadge";
import AdminEmptyState from "@/components/admin/AdminEmptyState";

export default function AdminInbox() {
  const { user } = useAuth();
  const [tab, setTab] = useState("ideas");
  const [counts, setCounts] = useState({ ideas: 0, suggestions: 0, inquiries: 0 });

  // School enquiries are admin-only. Teachers see ideas and suggestions only.
  const isAdmin = user?.role === "admin";

  const refreshCounts = async () => {
    try {
      const [ideas, suggestions] = await Promise.all([
        client.get("/community/ideas?limit=500"),
        client.get("/admin/suggestions"),
      ]);
      const next = {
        ideas: ideas.data.ideas.length,
        suggestions: suggestions.data.suggestions.filter((s) => !s.read).length,
        inquiries: 0,
      };
      if (isAdmin) {
        const { data } = await client.get("/admin/school-inquiries");
        next.inquiries = data.inquiries.filter((i) => !i.read).length;
      }
      setCounts(next);
    } catch { /* best-effort badge counts */ }
  };
  useEffect(() => { refreshCounts(); }, []);

  const TABS = [
    { value: "ideas", label: "Community Ideas", icon: Lightbulb, count: counts.ideas },
    { value: "suggestions", label: "Suggestions", icon: MessageCircleHeart, count: counts.suggestions },
    ...(isAdmin ? [{ value: "inquiries", label: "Inquiries", icon: School, count: counts.inquiries }] : []),
  ];

  return (
    <div data-testid="admin-inbox">
      <AdminPageHeader
        icon={Inbox}
        title="Moderation inbox"
        subtitle="Community ideas, parent suggestions, and school inquiries"
        actions={<AdminButton variant="secondary" icon={RefreshCw} onClick={refreshCounts}>Refresh</AdminButton>}
      />

      <AdminSegmentedControl options={TABS} value={tab} onChange={setTab} testId="inbox-tab" />

      <div className="mt-6">
        {tab === "ideas" && <CommunityIdeasModeration onChange={refreshCounts} />}
        {tab === "suggestions" && <SuggestionsInbox onChange={refreshCounts} />}
        {tab === "inquiries" && <SchoolInquiriesInbox onChange={refreshCounts} />}
      </div>
    </div>
  );
}

function CommunityIdeasModeration({ onChange }) {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/community/ideas?limit=500");
      setIdeas(data.ideas);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const hide = async (id) => {
    try {
      await client.put(`/admin/community/ideas/${id}/status`, { status: "hidden" });
      setIdeas((prev) => prev.filter((i) => i.id !== id));
      onChange?.();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  if (loading) return <p className="text-hv-slate text-sm">Loading…</p>;
  if (err) return <div className="text-hv-coral font-bold text-sm">{err}</div>;

  if (ideas.length === 0) {
    return <AdminEmptyState icon={Lightbulb} title="No open ideas." />;
  }

  return (
    <ul className="space-y-3" data-testid="inbox-ideas-list">
      {ideas.map((i) => (
        <li key={i.id} className="rounded-2xl p-5 bg-white border border-[#E5DFC8]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-hv-navy font-semibold">{i.text}</p>
              <p className="text-xs text-hv-slate mt-1">by {i.author_name} · {i.kind} · {i.vote_count} votes</p>
            </div>
            <AdminButton variant="destructive" size="sm" icon={EyeOff} onClick={() => hide(i.id)} className="shrink-0">
              Hide
            </AdminButton>
          </div>
        </li>
      ))}
    </ul>
  );
}

function SuggestionsInbox({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/suggestions");
      setItems(data.suggestions);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await client.put(`/admin/suggestions/${id}/read`);
      load();
      onChange?.();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const reply = async (id) => {
    const text = (replyDrafts[id] || "").trim();
    if (!text) return;
    try {
      await client.put(`/admin/suggestions/${id}/replied`, { reply: text });
      setReplyDrafts((d) => ({ ...d, [id]: "" }));
      load();
      onChange?.();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  if (loading) return <p className="text-hv-slate text-sm">Loading…</p>;
  if (err) return <div className="text-hv-coral font-bold text-sm">{err}</div>;

  if (items.length === 0) {
    return <AdminEmptyState icon={MessageCircleHeart} title="No suggestions yet." />;
  }

  return (
    <ul className="space-y-3" data-testid="inbox-suggestions-list">
      {items.map((s) => (
        <li key={s.id} className={`rounded-2xl p-5 border ${s.read ? "bg-white border-[#E5DFC8]" : "bg-hv-tealTint border-hv-teal"}`}>
          <div className="flex items-start gap-3">
            {!s.read && <AdminBadge tone="primary" uppercase className="mt-0.5">New</AdminBadge>}
            <div className="flex-1 min-w-0">
              <p className="text-hv-navy font-semibold">{s.text}</p>
              <p className="text-xs text-hv-slate mt-1">{s.user_name || s.user_email} · {new Date(s.created_at).toLocaleString()}</p>
              {s.reply && (
                <p className="mt-2 text-sm text-hv-teal inline-flex items-start gap-1">
                  <CornerDownRight size={14} className="mt-1 shrink-0" /> {s.reply}
                </p>
              )}
            </div>
            {!s.read && (
              <AdminButton variant="secondary" size="sm" onClick={() => markRead(s.id)} className="shrink-0">
                Mark read
              </AdminButton>
            )}
          </div>
          {!s.replied && (
            <div className="mt-3 flex gap-2">
              <input value={replyDrafts[s.id] || ""} onChange={(e) => setReplyDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                     placeholder="Reply…" className="flex-1 px-4 py-2 rounded-full border-2 border-[#E5DFC8] focus:border-hv-teal text-sm outline-none" />
              <AdminButton variant="primary" size="sm" onClick={() => reply(s.id)}>Reply</AdminButton>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function SchoolInquiriesInbox({ onChange }) {
  // The lightweight "Register your school" side door (Nav.js dialog + the
  // self-service "For my school" signup branch both post here) — a flat
  // list, not master-detail, since there's no roster attached, just enough
  // to start a conversation. "Create school" navigates to School Management
  // with ?prefill=<id>, which opens Create pre-filled but never auto-submits.
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/school-inquiries");
      setItems(data.inquiries);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await client.put(`/admin/school-inquiries/${id}/read`);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
      onChange?.();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove the inquiry from ${name}?`)) return;
    try {
      await client.delete(`/admin/school-inquiries/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      onChange?.();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  if (loading) return <p className="text-hv-slate text-sm">Loading…</p>;
  if (err) return <div className="text-hv-coral font-bold text-sm">{err}</div>;

  if (items.length === 0) {
    return <AdminEmptyState icon={School} title="No school inquiries yet." subtitle="These appear when someone uses 'Register your school' from the header." />;
  }

  return (
    <ul className="space-y-3" data-testid="inbox-inquiries-list">
      {items.map((i) => (
        <li key={i.id} className={`rounded-2xl p-5 border ${i.read ? "bg-white border-[#E5DFC8]" : "bg-hv-tealTint border-hv-teal"}`}
            onClick={() => !i.read && markRead(i.id)}>
          <div className="flex items-start gap-3">
            {!i.read && <AdminBadge tone="primary" uppercase className="mt-0.5">New</AdminBadge>}
            <div className="flex-1 min-w-0">
              <p className="text-hv-navy font-bold">{i.school_name}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-hv-slate">
                <span className="inline-flex items-center gap-1"><Mail size={12} /> {i.contact_email}</span>
                <span className="inline-flex items-center gap-1"><Phone size={12} /> {i.contact_phone}</span>
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {i.city}, {i.country}</span>
                <span className="inline-flex items-center gap-1"><Users size={12} /> ~{i.estimated_student_count} students</span>
              </div>
              <p className="text-xs text-hv-slate mt-1">{new Date(i.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <AdminButton variant="secondary" size="sm" icon={ArrowUpRight}
                           onClick={(e) => { e.stopPropagation(); navigate(`/admin/schools?prefill=${i.id}`); }}>
                Create school
              </AdminButton>
              <AdminButton variant="destructive" size="sm" icon={Trash2} onClick={(e) => { e.stopPropagation(); remove(i.id, i.school_name); }}>
                Remove
              </AdminButton>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
